import {BreakpointObserver} from '@angular/cdk/layout'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonToolbar,
  ModalController,
  NavController,
} from '@ionic/angular/standalone'
import {ModalAboutUserComponent} from '@common/components/modals/modal-about-user/modal-about-user.component'
import {LayoutBreakpointsObserver} from '@models/enums/layout-breakpoints-observer.enum'
import {UVEuser} from '@models/uve-user'
import {UveTranslatePipe} from '@pipes/uve-translate.pipe'
import {NetworkService} from '@service/network.service'
import {StorageApiService} from '@service/storage/storage-api.service'
import {RestSyncService} from '@service/sync/rest-sync.service'
import {TranslationServicex} from '@service/translationx.service'
import {MenuService} from '@service/ui/menu.service'
import {ModalCloseButtonComponent} from '../modal-close-button/modal-close-button.component'
import {LoggingService} from '@service/logging.service'
import {UveLogKey} from '@models/constants/log-key'

@Component({
  selector: 'app-menu',
  imports: [
    IonButtons,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonToolbar,
    ModalCloseButtonComponent,
    UveTranslatePipe,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent implements OnInit, OnDestroy {
  menuComponent = viewChild(IonMenu)

  // Required inputs
  user = input.required<UVEuser>()
  currentUrl = input.required<string>()
  version = input.required<string>()

  // Optional inputs
  swipeGesture = input(true)

  // Signals
  isMobile = signal(false)
  navigate = computed(() => this.menuService.navigate())
  isOnline = computed(() => this.networkService.isOnline())

  // Private properties
  private isSyncActive = false

  // Dependency injections
  private readonly navController = inject(NavController)
  private readonly modalController = inject(ModalController)
  private readonly translationServicex = inject(TranslationServicex)
  private readonly loggingService = inject(LoggingService)
  private readonly restSyncService = inject(RestSyncService)
  private readonly networkService = inject(NetworkService)
  private readonly menuService = inject(MenuService)
  private readonly breakpointObserver = inject(BreakpointObserver)
  private readonly destroyRef$ = inject(DestroyRef)
  private readonly storageApiService = inject(StorageApiService)

  ngOnInit(): void {
    this.menuService.init()

    this.breakpointObserver
      .observe(LayoutBreakpointsObserver.TABLET)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(result => this.isMobile.set(!result.matches))
  }

  ngOnDestroy(): void {
    this.menuService.destroy()
  }

  openModalUser = async (): Promise<void> => {
    await this.menuComponent().close()
    this.restSyncService.synchronizing = false

    if (this.isSyncActive) {
      return
    }

    this.isSyncActive = true

    const modal = await this.modalController.create({
      component: ModalAboutUserComponent,
      componentProps: {
        version: this.version(),
        isImpersonate: this.user()?.isImpersonate,
        user: this.user()?.userName,
        name: this.user()?.name,
        surname: this.user()?.surname,
      },
      cssClass: 'modal-about-user reset-safe-area-modal',
      backdropDismiss: false,
    })

    await modal.present()

    await modal.onDidDismiss()

    this.isSyncActive = false
  }

  close = (): void => {
    this.menuComponent().close()
  }

  goToSelectedPage = (page: any): void => {
    if (page.disableOffline() && !this.networkService.isOnline()) {
      return
    }
    const {title, url} = page

    const currentUrl = this.currentUrl()?.split('?')[0]
    if (url !== currentUrl) {
      this.insertTrace(title)
      if (page.action) {
        page.action()
      } else {
        this.navigateRoot(url)
      }
    }
  }

  goToConfiguration = (): void => {
    this.navigateRoot('/user-configuration')
  }

  private readonly navigateRoot = async (url: string): Promise<void> => {
    await this.menuComponent().close()

    this.navController.navigateRoot(url, {animated: true})
  }

  private readonly insertTrace = (title: string): void => {
    const name = this.translationServicex.instant(title)
    this.loggingService.debug(UveLogKey.Debug.MenuOption, {
      message: `goToMenuOption${name}`,
      value: name,
    })
  }
}
